import { useId } from 'react';
import NextLink from 'next/link';
import { Button } from '@savvy-kit/components/button';
import { List, ListItem, ListItemAction } from '@savvy-kit/components/list';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { MaskedValue } from '@savvy-kit/patterns/masked-value';
import Tag from '@savvy-kit/patterns/tag';
import { isNil } from 'es-toolkit';
import { GripVertical, Plus } from 'lucide-react';

import type { AccountGroupId, AddAccountItemType } from './constants';
import type { AccountRowDrag } from './hooks/use-account-row-drag';
import type { AccountGroup, AccountRow } from './types';
import { CLIENT_PORTAL_BIG_NUMBER_CLASS } from '../common/constants';
import { InstitutionAvatar } from '../common/institution-avatar';
import { ShimmerLine } from '../common/shimmer-line';
import { isPresent, safeMoney } from '../common/utils';
import { AccountsRowMenu } from './accounts-row-menu';
import {
  ACCOUNTS_GROUP_EMPTY_LABEL,
  ACCOUNTS_HELD_AWAY_MANAGEMENT_SUBTITLE,
  ACCOUNTS_MANAGED_SUBTITLE,
  ACCOUNTS_REORDER_HANDLE_LABEL,
  ACCOUNTS_UNKNOWN_PROVIDER_SUBTITLE,
  ROW_EXCLUDED_FROM_NET_WORTH_LABEL,
} from './constants';
import { useAccountRowDrag } from './hooks/use-account-row-drag';
import { orderPosition } from './row-order';
import { accountMaskLabel, rowUpdatedLabel } from './utils';

const SUBTITLE_SEPARATOR = ' · ';
const ROW_GRID_CLASS = cn(
  'relative grid min-w-0 flex-1 items-center gap-x-3 gap-y-1.5 py-3.5',
  'grid-cols-[auto_minmax(0,1fr)_auto]',
  'md:grid-cols-[auto_minmax(0,auto)_1fr_auto] md:gap-y-0.5',
);
const ROW_FULL_WIDTH_CELL_CLASS = 'col-span-3 md:col-span-1';
const ROW_NAME_CELL_CLASS = 'md:col-start-2 md:row-start-1';
const ROW_MASKED_NUMBER_CLASS = 'relative z-10 text-xs';
const ROW_LINK_CLASS =
  'absolute inset-0 rounded-sm focus-visible:ring-2 focus-visible:ring-border-light focus-visible:outline-none';
const ROW_REORDERABLE_CLASS = 'touch-pan-y select-none [-webkit-touch-callout:none]';
const ROW_LIFTED_CLASS = 'z-20 rounded-lg bg-card shadow-xl';
const ROW_SETTLE_CLASS = 'transition-[background-color,transform] duration-200 ease-out';
const HANDLE_CLASS = 'hidden h-6 w-5 shrink-0 items-center justify-center md:flex';
const HANDLE_REORDERABLE_CLASS = cn(
  'flex size-11 cursor-grab touch-none text-secondary active:cursor-grabbing',
  'md:h-6 md:w-5 md:opacity-0 md:transition-opacity md:group-hover/row:opacity-100',
);

function accountCountLabel(count: number) {
  return `${count} ${count === 1 ? 'account' : 'accounts'}`;
}

function rowProvider(row: AccountRow) {
  if (isPresent(row.institution)) {
    return row.institution;
  }
  return row.kind === 'account' && !row.heldAway ? ACCOUNTS_UNKNOWN_PROVIDER_SUBTITLE : null;
}

function rowManagement(row: AccountRow) {
  if (row.managedBySavvy) {
    return ACCOUNTS_MANAGED_SUBTITLE;
  }
  return row.heldAway ? ACCOUNTS_HELD_AWAY_MANAGEMENT_SUBTITLE : null;
}

function rowSubtitleText(row: AccountRow) {
  const managed = rowManagement(row);
  const renamedFrom = isPresent(row.customName) ? row.sourceName : null;
  const number = isPresent(row.accountNumber) ? null : accountMaskLabel(row.mask);
  const parts = [managed, renamedFrom, rowProvider(row), number].filter(isPresent);
  return parts.length > 0 ? parts.join(SUBTITLE_SEPARATOR) : null;
}

function RowSubtitle({ row, className }: { readonly row: AccountRow; readonly className?: string }) {
  const text = rowSubtitleText(row);

  if (!isPresent(row.accountNumber)) {
    return isPresent(text) ? (
      <Text variant="caption" color="secondary" className={cn('md:truncate', className)}>
        {text}
      </Text>
    ) : null;
  }

  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-1', className)}>
      {isPresent(text) && (
        <Text variant="caption" color="secondary" className="md:truncate">
          {`${text} ·`}
        </Text>
      )}
      <MaskedValue value={row.accountNumber} size="sm" className={ROW_MASKED_NUMBER_CLASS} />
    </div>
  );
}

type AccountRowsProps = {
  readonly rows: readonly AccountRow[];
  readonly pendingCustomNameId: string | null;
  readonly canManageRows: boolean;
  readonly canReorder: boolean;
  readonly onRefreshBalances: () => void;
  readonly onRenameRow: (row: AccountRow) => void;
  readonly onUpdateRowBalance: (row: AccountRow) => void;
  readonly onRemoveRow: (row: AccountRow) => Promise<void>;
  readonly onToggleRowInNetWorth: (row: AccountRow) => Promise<void>;
  readonly onDropRowOnto: (movedId: string, targetId: string) => void;
  readonly onNudgeRow: (rowId: string, offset: number) => void;
};

function rowLinkLabel(row: AccountRow) {
  return `${row.name}, ${safeMoney(row.balance)}`;
}

function rowStamp(row: AccountRow) {
  return row.countsTowardNetWorth ? rowUpdatedLabel(row.lastUpdatedAt) : ROW_EXCLUDED_FROM_NET_WORTH_LABEL;
}

function RowBody({ row, namePending }: { readonly row: AccountRow; readonly namePending: boolean }) {
  const stamp = rowStamp(row);

  return (
    <div className={ROW_GRID_CLASS}>
      <NextLink href={row.href} draggable={false} className={ROW_LINK_CLASS} aria-label={rowLinkLabel(row)} />
      <span className="flex md:row-span-2 md:row-start-1">
        <InstitutionAvatar
          name={isPresent(row.institution) ? row.institution : row.name}
          logoUrl={row.logoUrl}
          shape="circle"
          size="md"
        />
      </span>
      {namePending ? (
        <ShimmerLine className={cn('h-5 w-40', ROW_NAME_CELL_CLASS)} />
      ) : (
        <Text variant="body-sm" color="dark" className={cn('min-w-0 font-medium md:truncate', ROW_NAME_CELL_CLASS)}>
          {row.name}
        </Text>
      )}
      <Text
        variant="body-sm"
        color={!isNil(row.balance) && row.balance < 0 ? 'destructive' : 'dark'}
        className="text-right font-medium tabular-nums md:col-start-4 md:row-start-1"
        render={<span />}
      >
        {safeMoney(row.balance)}
      </Text>
      {isPresent(row.typeLabel) && (
        <span
          className={cn(
            ROW_FULL_WIDTH_CELL_CLASS,
            'flex flex-wrap items-center gap-2 md:col-start-3 md:row-start-1 md:justify-self-start',
          )}
        >
          <Tag>{row.typeLabel}</Tag>
        </span>
      )}
      <RowSubtitle row={row} className={cn(ROW_FULL_WIDTH_CELL_CLASS, 'md:col-span-2 md:col-start-2 md:row-start-2')} />
      {isPresent(stamp) && (
        <Text
          variant="caption"
          color="secondary"
          className={cn(ROW_FULL_WIDTH_CELL_CLASS, 'md:col-start-4 md:row-start-2 md:justify-self-end')}
        >
          {stamp}
        </Text>
      )}
    </div>
  );
}

function rowDragState(drag: AccountRowDrag, rowId: string) {
  if (drag.liftedId !== rowId) {
    return undefined;
  }
  return drag.settling ? 'settling' : 'lifted';
}

function rowDisplacementStyle(drag: AccountRowDrag, rowId: string) {
  if (drag.liftedId === rowId) {
    return undefined;
  }
  const displacement = drag.displacementOf(rowId);
  return displacement === 0 ? undefined : { transform: `translateY(${displacement}px)` };
}

function AccountRows({
  rows,
  pendingCustomNameId,
  canManageRows,
  canReorder,
  onRefreshBalances,
  onRenameRow,
  onUpdateRowBalance,
  onRemoveRow,
  onToggleRowInNetWorth,
  onDropRowOnto,
  onNudgeRow,
}: AccountRowsProps) {
  const drag = useAccountRowDrag(canReorder, onDropRowOnto);
  const rowIds = rows.map((row) => row.id);

  return (
    <List ref={drag.listRef} className={cn('bg-card', !isNil(drag.liftedId) && 'overflow-visible')}>
      {rows.map((row) => (
        <ListItem
          key={row.id}
          data-account-row-id={row.id}
          data-drag-state={rowDragState(drag, row.id)}
          className={cn(
            'group/row relative gap-1 p-0 pl-3 md:pl-2',
            'hover:bg-component-hover',
            drag.suppressTransitions ? 'transition-none' : ROW_SETTLE_CLASS,
            canReorder && ROW_REORDERABLE_CLASS,
            drag.liftedId === row.id && ROW_LIFTED_CLASS,
          )}
          style={rowDisplacementStyle(drag, row.id)}
          onPointerDown={(event) => {
            drag.onRowPointerDown(row.id, event);
          }}
          onPointerMove={drag.onRowPointerMove}
          onPointerUp={drag.onRowPointerUp}
          onClickCapture={drag.onRowClickCapture}
          onContextMenu={drag.onRowContextMenu}
        >
          <span
            aria-hidden="true"
            title={canReorder ? ACCOUNTS_REORDER_HANDLE_LABEL : undefined}
            onPointerDown={(event) => {
              drag.onHandlePointerDown(row.id, event);
            }}
            className={cn(HANDLE_CLASS, canReorder && HANDLE_REORDERABLE_CLASS)}
          >
            {canReorder && <GripVertical size={16} />}
          </span>
          <RowBody row={row} namePending={row.id === pendingCustomNameId} />
          <ListItemAction className="w-9 justify-center pr-1">
            {canManageRows && (
              <AccountsRowMenu
                row={row}
                position={orderPosition(rowIds, row.id)}
                canReorder={canReorder}
                canManage={!row.managedBySavvy}
                canRename={row.kind === 'account'}
                onRefresh={onRefreshBalances}
                onRename={onRenameRow}
                onUpdateBalance={onUpdateRowBalance}
                onRemove={onRemoveRow}
                onToggleNetWorth={onToggleRowInNetWorth}
                onNudge={(offset) => {
                  onNudgeRow(row.id, offset);
                }}
              />
            )}
          </ListItemAction>
        </ListItem>
      ))}
    </List>
  );
}

type AccountsGroupSectionProps = {
  readonly group: AccountGroup;
  readonly pendingCustomNameId: string | null;
  readonly canConnect: boolean;
  readonly onConnect: (itemType: AddAccountItemType, feedOnly?: boolean) => void;
  readonly canManageRows: boolean;
  readonly onRefreshBalances: () => void;
  readonly onRenameRow: (row: AccountRow) => void;
  readonly onUpdateRowBalance: (row: AccountRow) => void;
  readonly onRemoveRow: (row: AccountRow) => Promise<void>;
  readonly onToggleRowInNetWorth: (row: AccountRow) => Promise<void>;
  readonly onDropRowOnto: (
    groupId: AccountGroupId,
    rowIds: readonly string[],
    movedId: string,
    targetId: string,
  ) => void;
  readonly onNudgeRow: (groupId: AccountGroupId, rowIds: readonly string[], movedId: string, offset: number) => void;
};

export function AccountsGroupSection({
  group,
  pendingCustomNameId,
  canConnect,
  onConnect,
  canManageRows,
  onRefreshBalances,
  onRenameRow,
  onUpdateRowBalance,
  onRemoveRow,
  onToggleRowInNetWorth,
  onDropRowOnto,
  onNudgeRow,
}: AccountsGroupSectionProps) {
  const negativeTotal = !isNil(group.total) && group.total < 0;
  const headingId = useId();
  const rowIds = group.rows.map((row) => row.id);
  const canReorder = canManageRows && group.rows.length > 1;
  const isEmpty = group.rows.length === 0;

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <Text variant="subtitle-lg" color="dark" className="truncate" render={<h2 id={headingId} />}>
            {group.label}
          </Text>
          <Text variant="body-sm">{accountCountLabel(group.rows.length)}</Text>
        </div>
        {isEmpty ? (
          <Text variant="body-sm">{ACCOUNTS_GROUP_EMPTY_LABEL}</Text>
        ) : (
          <Text
            color={negativeTotal ? 'destructive' : 'dark'}
            className={cn(CLIENT_PORTAL_BIG_NUMBER_CLASS, 'shrink-0 text-xl md:text-2xl')}
            aria-label={`${group.label} total`}
            render={<span />}
          >
            {safeMoney(group.total)}
          </Text>
        )}
      </div>
      {!isEmpty && (
        <AccountRows
          rows={group.rows}
          pendingCustomNameId={pendingCustomNameId}
          canManageRows={canManageRows}
          canReorder={canReorder}
          onRefreshBalances={onRefreshBalances}
          onRenameRow={onRenameRow}
          onUpdateRowBalance={onUpdateRowBalance}
          onRemoveRow={onRemoveRow}
          onToggleRowInNetWorth={onToggleRowInNetWorth}
          onDropRowOnto={(movedId, targetId) => {
            onDropRowOnto(group.id, rowIds, movedId, targetId);
          }}
          onNudgeRow={(rowId, offset) => {
            onNudgeRow(group.id, rowIds, rowId, offset);
          }}
        />
      )}
      {canConnect && (
        <div className="mt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              onConnect(group.connectItemType, group.connectFeedOnly);
            }}
          >
            <Plus size={14} aria-hidden="true" />
            {group.connectLabel}
          </Button>
        </div>
      )}
    </section>
  );
}
